/* eslint-disable @typescript-eslint/no-explicit-any */
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import componentsApi from '../../../../api/components';
import LoadingSpinner from '../../../../components/loadingSpinner/LoadingSpinner';
import ConfirmModal from '../../../../components/modal/ConfirmModal';
import useComponents from '../../../../datastore/useComponents';
import useProducts from '../../../../datastore/useProducts';
import useAlert from '../../../../hooks/useAlert';
import useLoadingModal from '../../../../hooks/useLoadingModal';
import useRequest from '../../../../hooks/useRequest';
import { AdminHeader } from '../../components/AdminHeader';

export default function ComponentArchive() {
    const { componentId } = useParams();
    const id = Number(componentId);
    const { components, fetchComponents } = useComponents();
    const { products } = useProducts()
    const { setLoading } = useLoadingModal()
    const { data: productVariants, loading: productVariantsLoading } = useRequest(() => componentsApi.activeProductVariantsFor(id), [componentId], isNaN(id))
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const { showAlert } = useAlert()
    useEffect(() => { fetchComponents() }, [])

    const component = (components || []).find(c => c.id === id);
    const hasActiveProductVariants = (productVariants || []).length > 0;

    const confirmArchive = () => {
        if (hasActiveProductVariants) {
            showAlert('There are still active product variants using this component', 'error')
            return
        }
        setShowConfirmModal(true)
    }

    async function archive() {
        try {
            setLoading(true)
            await componentsApi.archive(id);
            fetchComponents();
            setShowConfirmModal(false);
            showAlert('Component archived successfully', 'success')
        } catch (err: any) {
            console.error(err)
            showAlert(err.data ?? err.message ?? 'Error archiving component', 'error')
        } finally {
            setLoading(false)
        }
    }



    return (
        <Stack
            sx={{
                height: '100vh',
                overflow: 'hidden',
            }}
        >
            <AdminHeader
                title={'Archive Component'}
                description={`Permanently archive ${component?.name ?? 'this component'}`}
                icon={<ShoppingCartIcon fontSize="large" sx={{ color: 'white' }} />}
                buttonText="Archive"
                buttonAction={confirmArchive}
            />
            <Stack
                sx={{
                    flex: 1,
                    overflow: 'hidden',
                    padding: 2,
                }}
            >
                <Typography variant='h2'>Active Product Variants</Typography>
                <Typography variant='body1'>Archiving is not supported with active product variants{hasActiveProductVariants ? ', please review' : ''}</Typography>
                <Link href='/admin/components'>Back to Components</Link>
                <Table sx={{ maxWidth: '100%', overflowX: 'auto' }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Component</TableCell>
                            <TableCell>Product</TableCell>
                            <TableCell>Is Core</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            productVariantsLoading ? (
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <LoadingSpinner isLoading />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                (productVariants || []).map((variant) => {
                                    return (
                                        <TableRow
                                            key={variant.id}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            <TableCell>{variant.name}</TableCell>
                                            <TableCell><Link sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start'
                                            }} href={`/admin/products/${variant.productId}`}>{products.find(p => p.id === variant.productId)?.name} <ArrowRightAltIcon /></Link></TableCell>
                                            <TableCell>{variant.isCore ? 'Yes' : 'No'}</TableCell>
                                        </TableRow>
                                    )
                                })
                            )
                        }
                        {
                            !productVariantsLoading && !productVariants?.length && (
                                <TableRow>
                                    <TableCell colSpan={3} sx={{ textAlign: 'center' }}>No active product variants</TableCell>
                                </TableRow>
                            )
                        }

                    </TableBody>
                </Table>
            </Stack>
            {
                showConfirmModal && (
                    <ConfirmModal
                        title={`Archive ${component?.name ?? 'this component'}`}
                        content={`Are you sure you want to PERMANENTLY ARCHIVE ${component?.name ?? 'this component'}?`}
                        onClose={() => setShowConfirmModal(false)}
                        onConfirm={archive}
                    />
                )
            }
        </Stack>
    )
}